/*
   The intake sheet, connected.

   The sheet is presentational: every decision arrives as a prop. This holds the
   three calls behind it — reading the printed label, listing EPA's masters for
   a registration, and queueing the review. Form state lives in
   `use-intake-form`.

   Attaching a master here is optional by design. Which master governs is the
   server's decision, taken when the pipeline runs; skipping step 2 leaves that
   answer just as correct, only later. `governingHint` shows where the pick is
   expected to land so the choice is informed, not so the browser makes it.
*/

import { useQuery } from "@tanstack/react-query"
import * as React from "react"

import { detectorProfileOptionsQuery } from "@/components/domain/detector-profile"

import {
  batchPolicyQuery,
  engagementsQuery,
  datedNewestFirst,
  governingHint,
  mastersQuery,
  useCreateReview,
  useIdentifyLabel,
  type LabelDocument,
} from "./api"
import { NewReviewSheet, type MasterCandidate, type ResolveState } from "./new-review-sheet"
import { usePageCount } from "./use-page-count"
import { useIntakeForm } from "./use-intake-form"

import { readAuthSession } from "@/lib/auth-token"
import { ApiError } from "@/lib/errors"
import { canChooseDetectorSettings, canListClients, isClient, toRole } from "@/lib/roles"

/** EPA's own stamp (YYYYMMDD) rendered the way the rest of the console reads dates. */
function formatStamp(doc: LabelDocument): string {
  if (doc.accepted_date) return doc.accepted_date
  const raw = doc["stamp_date"]
  if (typeof raw !== "string" || raw.length !== 8) return "date unknown"
  return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`
}

function toCandidate(doc: LabelDocument, reason?: string): MasterCandidate {
  // The URL is the identity: it is what the server is sent, and what keeps a
  // pick unambiguous when two entries share an accepted date.
  return {
    id: doc.url,
    label: doc.pdffile,
    stampedOn: formatStamp(doc),
    source: "ppls",
    ...(reason === undefined ? {} : { reason }),
  }
}

export function NewReviewContainer({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  /** Where to go once the review is queued. The sheet does not route itself. */
  onCreated: (reviewId: number) => void
}): JSX.Element {
  const { form, set, fillIfBlank, reset: resetForm } = useIntakeForm()

  const role = toRole(readAuthSession()?.user.role)
  const canPickClient = canListClients(role)
  const engagements = useQuery(engagementsQuery(open && canPickClient))
  const masters = useQuery(mastersQuery(form.searched))
  const identify = useIdentifyLabel()
  const create = useCreateReview()
  const batchPolicy = useQuery(batchPolicyQuery(open))
  const pageCount = usePageCount(form.printedFile)
  const canChooseDetectors = canChooseDetectorSettings(role)
  const detectorOptions = useQuery(detectorProfileOptionsQuery(open && canChooseDetectors))

  const reset = React.useCallback(() => {
    resetForm()
    identify.reset()
    create.reset()
  }, [resetForm, identify, create])

  const resolve = React.useMemo((): ResolveState => {
    if (!form.searched) return { kind: "idle" }
    if (masters.isPending) return { kind: "resolving" }
    if (masters.isError) {
      // EPA answers US addresses only, so a failure is usually egress rather
      // than a missing product. A 404 is the one that means the registration
      // genuinely has nothing on record.
      const status = masters.error instanceof ApiError ? masters.error.status : 0
      return status === 404 ? { kind: "not_found" } : { kind: "unreachable" }
    }

    const documents = masters.data.documents
    const hint = governingHint(documents, form.printedOn)
    // A registration EPA knows but holds no dated documents for reads the same
    // to a reviewer as one it does not know: nothing here to judge against.
    if (!hint) return { kind: "not_found" }

    return {
      kind: "resolved",
      regno: masters.data.regno,
      // Ordered here, not taken on trust: the picker tells the reviewer the list
      // is newest first, so the two must not be able to disagree.
      candidates: datedNewestFirst(documents).map((d) => toCandidate(d.document)),
      governing: toCandidate(
        hint.document,
        hint.caveat ??
          "Approved on or before the printed date, so this is the version in force when the label was printed.",
      ),
    }
  }, [
    form.searched,
    form.printedOn,
    masters.isPending,
    masters.isError,
    masters.error,
    masters.data,
  ])

  return (
    <NewReviewSheet
      open={open}
      onOpenChange={(o) => {
        if (!o) reset()
        onOpenChange(o)
      }}
      printedFile={form.printedFile}
      printedStatus={form.printedStatus}
      identifying={identify.isPending}
      identifiedName={form.identifiedName}
      identifyFailed={identify.isError}
      onPrintedSelect={(f) => {
        set({ printedFile: f, printedStatus: "ready" })
        // Read the label so the reviewer does not retype what is printed on it.
        identify.mutate(f, {
          onSuccess: (found) => {
            fillIfBlank({
              ...(found.reg_nr ? { regNo: found.reg_nr } : {}),
              ...(found.fpl_date ? { printedOn: found.fpl_date } : {}),
            })
            set({ identifiedName: found.product_name ?? null })
          },
        })
      }}
      onPrintedRemove={() => {
        set({ printedFile: null, printedStatus: "idle", identifiedName: null })
        identify.reset()
      }}
      regNo={form.regNo}
      onRegNoChange={(regNo) => {
        set({ regNo })
      }}
      printedOn={form.printedOn}
      onPrintedOnChange={(printedOn) => {
        set({ printedOn })
      }}
      resolve={resolve}
      onResolve={() => {
        set({ selectedMasterIds: [], searched: form.regNo.trim() })
      }}
      selectedMasterIds={form.selectedMasterIds}
      onToggleMaster={(id) => {
        // Order is meaning: the endpoint treats the first attached master as
        // governing, so a toggle appends rather than reordering what is there.
        set({
          selectedMasterIds: form.selectedMasterIds.includes(id)
            ? form.selectedMasterIds.filter((held) => held !== id)
            : [...form.selectedMasterIds, id],
        })
      }}
      masterFiles={form.masterFiles}
      masterStatus={form.masterStatus}
      onMasterSelect={(f) => {
        // The zone takes one at a time; attaching appends rather than replacing,
        // because a label can draw on more than one approved master.
        set({
          masterFiles: form.masterFiles.some((held) => held.name === f.name)
            ? form.masterFiles
            : [...form.masterFiles, f],
          masterStatus: "ready",
        })
      }}
      onMasterRemove={(name) => {
        const remaining = form.masterFiles.filter((f) => f.name !== name)
        set({
          masterFiles: remaining,
          ...(remaining.length === 0 ? { masterStatus: "idle" as const } : {}),
        })
      }}
      clients={(engagements.data ?? []).map((e) => ({ value: String(e.id), label: e.name }))}
      clientsLoading={engagements.isLoading}
      canPickClient={canPickClient}
      isClient={isClient(role)}
      clientId={form.clientId}
      onClientChange={(clientId) => {
        set({ clientId })
      }}
      batch={form.batch}
      onBatchChange={(batch) => {
        set({ batch })
      }}
      // Null while the policy is unknown and when the lane is off here: a
      // control the server will ignore is worse than no control.
      batchPolicy={
        batchPolicy.data?.enabled
          ? {
              pageThreshold: batchPolicy.data.page_threshold,
              ceilingHours: batchPolicy.data.ceiling_hours,
            }
          : null
      }
      pageCount={pageCount.kind === "counted" ? pageCount.pages : null}
      detectorSettings={
        canChooseDetectors
          ? {
              options: detectorOptions.data,
              unavailable: detectorOptions.isError,
              value: form.detectorProfile,
              onChange: (detectorProfile) => {
                set({ detectorProfile })
              },
            }
          : null
      }
      submitting={create.isPending}
      {...(create.error ? { submitError: create.error.message } : {})}
      onSubmit={() => {
        if (!form.printedFile) return
        // A picked PPLS master goes as a URL, never as bytes: the browser
        // cannot reach EPA, and the server refuses any host but PPLS.
        create.mutate(
          {
            fpl: form.printedFile,
            ...(form.masterFiles.length > 0 ? { masters: form.masterFiles } : {}),
            ...(form.selectedMasterIds.length > 0 ? { masterUrls: form.selectedMasterIds } : {}),
            ...(form.regNo.trim() ? { regNr: form.regNo.trim() } : {}),
            ...(form.printedOn ? { fplDate: form.printedOn } : {}),
            ...(form.clientId ? { clientId: Number(form.clientId) } : {}),
            ...(form.batch === undefined ? {} : { batch: form.batch }),
            detectorProfile: form.detectorProfile,
          },
          {
            onSuccess: (review) => {
              onOpenChange(false)
              reset()
              onCreated(review.id)
            },
          },
        )
      }}
    />
  )
}
